import { useEffect } from 'react';
import { Minus, Plus } from '../../../../modules/design-system';

export interface QuantityStepperProps {
  value: number;
  min?: number;
  max?: number;
  onChange: (next: number) => void;
  /** card — bare buttons inside the desktop price card; dock — the phone's
   *  row, where the stepper sits in its own glass capsule. */
  variant?: 'card' | 'dock';
  /** Locks both buttons, e.g. while the pack is restocking. */
  disabled?: boolean;
}

const VARIANTS = {
  card: { pod: 'gap-[11px]', btn: 'h-11 w-11' },
  dock: { pod: 'h-[46px] gap-1.5 rounded-full px-1 glass-ios-material', btn: 'h-[38px] w-[38px]' },
} as const;

/**
 * [−] N [+] of the glass storefront (repackz-handoff §8.1): round glass
 * buttons and a tabular digit so 1 → 2 → 10 never shifts the row. Fills
 * flow through the shared `--co-QuantityStepper-*` knobs.
 */
export function QuantityStepper({
  value,
  min = 1,
  max = 99,
  onChange,
  variant = 'card',
  disabled = false,
}: QuantityStepperProps) {
  const v = VARIANTS[variant];
  const canDec = !disabled && value > min;
  const canInc = !disabled && value < max;

  // Keep the value inside [min, max]. While max is still loading (< min),
  // leave the value alone rather than dragging it to 0.
  useEffect(() => {
    if (value < min) {
      onChange(min);
      return;
    }
    if (max >= min && value > max) {
      onChange(max);
    }
  }, [value, min, max, onChange]);

  const btnClass =
    `${v.btn} relative z-[1] flex shrink-0 cursor-pointer items-center justify-center rounded-full ` +
    'backdrop-blur-[17px] transition-transform active:scale-95 ' +
    'disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100';
  const btnStyle = {
    backgroundColor: 'var(--co-QuantityStepper-buttonFill, rgba(255,255,255,0.13))',
    color: 'var(--co-QuantityStepper-buttonIcon, #ffffff)',
  };

  return (
    <div
      data-co-component="QuantityStepper"
      className={`relative flex shrink-0 items-center ${v.pod}`}
      style={{ display: 'var(--co-QuantityStepper-display, flex)' }}
    >
      <button
        type="button"
        onClick={() => canDec && onChange(value - 1)}
        disabled={!canDec}
        aria-label="Decrease quantity"
        className={btnClass}
        style={btnStyle}
      >
        <Minus size={18} />
      </button>
      <span
        className="relative z-[1] min-w-[1.5ch] text-center text-[15px] font-bold leading-4 tabular-nums"
        style={{ color: 'var(--co-QuantityStepper-valueText, #ffffff)' }}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={() => canInc && onChange(value + 1)}
        disabled={!canInc}
        aria-label="Increase quantity"
        className={btnClass}
        style={btnStyle}
      >
        <Plus size={18} />
      </button>
    </div>
  );
}
